import { useState, useEffect } from 'react';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../../components/ui/dialog';
import { Building2, Plus, Loader2, Users, ShoppingBag, FileText, Settings, Check, X, Save, Pencil, Trash2, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../../contexts/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const AdminAgencies = () => {
  const { isGroupSA } = useAuth();
  const [agencies, setAgencies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [selectedAgency, setSelectedAgency] = useState(null);
  const [agencySettings, setAgencySettings] = useState(null);
  const [fdvTemplates, setFdvTemplates] = useState([]);
  const [form, setForm] = useState({ name: '', slug: '', logo_url: '' });
  const [submitting, setSubmitting] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [editForm, setEditForm] = useState({ agency_id: '', name: '', logo_url: '' });
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteCode, setDeleteCode] = useState('');
  const [deleteInput, setDeleteInput] = useState('');

  useEffect(() => { fetchAgencies(); }, []);

  const fetchAgencies = async () => {
    setLoading(true);
    try {
      const r = await fetch(`${API}/agencies`, { credentials: 'include' });
      if (r.ok) setAgencies(await r.json());
    } finally {
      setLoading(false);
    }
  };

  const createAgency = async () => {
    if (!form.name.trim() || !form.slug.trim()) {
      toast.error('Nom et slug obligatoires');
      return;
    }
    setSubmitting(true);
    try {
      const r = await fetch(`${API}/agencies`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      if (r.ok) {
        toast.success('Filiale creee');
        setCreateOpen(false);
        setForm({ name: '', slug: '', logo_url: '' });
        fetchAgencies();
      } else {
        const err = await r.json();
        toast.error(err.detail || 'Erreur');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const toggleActive = async (agency) => {
    try {
      const r = await fetch(`${API}/agencies/${agency.agency_id}`, {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ is_active: !agency.is_active }),
      });
      if (r.ok) {
        toast.success(agency.is_active ? 'Filiale desactivee' : 'Filiale activee');
        fetchAgencies();
      }
    } catch (e) {
      toast.error('Erreur');
    }
  };

  const openEdit = (agency) => {
    setEditForm({ agency_id: agency.agency_id, name: agency.name, logo_url: agency.logo_url || '' });
    setEditOpen(true);
  };

  const saveEdit = async () => {
    setSubmitting(true);
    try {
      const r = await fetch(`${API}/agencies/${editForm.agency_id}`, {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: editForm.name, logo_url: editForm.logo_url }),
      });
      if (r.ok) { toast.success('Filiale mise a jour'); setEditOpen(false); fetchAgencies(); }
      else toast.error((await r.json()).detail || 'Erreur');
    } finally { setSubmitting(false); }
  };

  const openDelete = (agency) => {
    setDeleteTarget(agency);
    setDeleteCode(Math.random().toString(36).slice(2, 8).toUpperCase());
    setDeleteInput('');
  };

  const confirmDelete = async () => {
    if (deleteInput.trim().toUpperCase() !== deleteCode) { toast.error('Code incorrect'); return; }
    setSubmitting(true);
    try {
      const r = await fetch(`${API}/agencies/${deleteTarget.agency_id}`, { method: 'DELETE', credentials: 'include' });
      if (r.ok) { toast.success('Filiale supprimee'); setDeleteTarget(null); fetchAgencies(); }
      else toast.error((await r.json()).detail || 'Erreur');
    } finally { setSubmitting(false); }
  };

  const openSettings = async (agency) => {
    setSelectedAgency(agency);
    setAgencySettings(null);
    setSettingsOpen(true);
    try {
      const r = await fetch(`${API}/agencies/${agency.agency_id}/settings`, { credentials: 'include' });
      if (r.ok) setAgencySettings(await r.json());
      const rt = await fetch(`${API}/fdv-templates`, { credentials: 'include' });
      if (rt.ok) setFdvTemplates(await rt.json());
    } catch (e) {
      toast.error('Erreur chargement parametres');
    }
  };

  const saveSettings = async () => {
    if (!agencySettings) return;
    setSubmitting(true);
    try {
      const { _id, ...payload } = agencySettings;
      const r = await fetch(`${API}/agencies/${selectedAgency.agency_id}/settings`, {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (r.ok) {
        toast.success('Parametres enregistres');
        setSettingsOpen(false);
      } else {
        const err = await r.json();
        toast.error(err.detail || 'Erreur');
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 font-[Manrope]" data-testid="admin-agencies-title">
            Filiales du Groupe LNC
          </h1>
          <p className="text-slate-500 mt-1">
            Chaque filiale a ses propres utilisateurs, ventes et parametres.
          </p>
        </div>

        {isGroupSA && (
          <Dialog open={createOpen} onOpenChange={setCreateOpen}>
            <DialogTrigger asChild>
              <Button className="bg-indigo-600 hover:bg-indigo-700" data-testid="create-agency-btn">
                <Plus className="w-4 h-4 mr-2" /> Nouvelle filiale
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Creer une filiale</DialogTitle>
                <DialogDescription>
                  Une filiale isole ses utilisateurs, ventes, commissions et parametres.
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4 py-2">
                <div className="space-y-2">
                  <Label>Nom *</Label>
                  <Input
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value, slug: form.slug || e.target.value.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '') })}
                    placeholder="Ex: EduPlan-LNC"
                    data-testid="agency-name-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Slug (identifiant court, unique)</Label>
                  <Input
                    value={form.slug}
                    onChange={(e) => setForm({ ...form, slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '') })}
                    placeholder="eduplan-lnc"
                    data-testid="agency-slug-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>URL du logo (optionnel)</Label>
                  <Input
                    value={form.logo_url}
                    onChange={(e) => setForm({ ...form, logo_url: e.target.value })}
                    placeholder="https://..."
                  />
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setCreateOpen(false)}>Annuler</Button>
                <Button
                  onClick={createAgency}
                  disabled={submitting}
                  className="bg-indigo-600 hover:bg-indigo-700"
                  data-testid="submit-agency-btn"
                >
                  {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                  Creer
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        )}
      </div>

      {!isGroupSA && (
        <div className="p-4 rounded-xl bg-amber-50 border border-amber-200 text-sm text-amber-800">
          Seul le Super Admin du Groupe (transverse) peut creer/desactiver des filiales.
          Vous consultez uniquement votre filiale.
        </div>
      )}

      <Card className="border-0 shadow-sm">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50">
                <TableHead>Filiale</TableHead>
                <TableHead>Slug</TableHead>
                <TableHead>Utilisateurs</TableHead>
                <TableHead>Ventes</TableHead>
                <TableHead>FDC</TableHead>
                <TableHead>Statut</TableHead>
                <TableHead>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow><TableCell colSpan={7} className="text-center py-8"><Loader2 className="w-5 h-5 animate-spin inline" /></TableCell></TableRow>
              ) : agencies.length === 0 ? (
                <TableRow><TableCell colSpan={7} className="text-center py-8 text-slate-500">Aucune filiale</TableCell></TableRow>
              ) : (
                agencies.map((ag) => (
                  <TableRow key={ag.agency_id} data-testid={`agency-row-${ag.slug}`}>
                    <TableCell>
                      <div className="flex items-center gap-3">
                        {ag.logo_url ? (
                          <img src={ag.logo_url} alt="" className="w-9 h-9 rounded-lg object-cover" />
                        ) : (
                          <div className="w-9 h-9 rounded-lg bg-indigo-100 flex items-center justify-center">
                            <Building2 className="w-5 h-5 text-indigo-600" />
                          </div>
                        )}
                        <div>
                          <p className="font-medium">{ag.name}</p>
                          <p className="text-xs text-slate-400">{ag.agency_id}</p>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell><code className="text-xs bg-slate-100 px-2 py-1 rounded">{ag.slug}</code></TableCell>
                    <TableCell>
                      <div className="text-sm">
                        <div className="flex items-center gap-1"><Users className="w-3 h-3 text-slate-400" />{ag.stats?.users || 0}</div>
                        <div className="text-xs text-slate-500 mt-0.5">
                          {ag.stats?.soa || 0} SOA · {ag.stats?.rh || 0} RH · {ag.stats?.commercials || 0} AC
                        </div>
                      </div>
                    </TableCell>
                    <TableCell><ShoppingBag className="w-3 h-3 inline text-slate-400 mr-1" />{ag.stats?.sales || 0}</TableCell>
                    <TableCell><FileText className="w-3 h-3 inline text-slate-400 mr-1" />{ag.stats?.fdc || 0}</TableCell>
                    <TableCell>
                      {ag.is_active ? (
                        <Badge className="bg-emerald-100 text-emerald-700">Active</Badge>
                      ) : (
                        <Badge className="bg-slate-200 text-slate-600">Inactive</Badge>
                      )}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1">
                        <Button variant="ghost" size="sm" onClick={() => openSettings(ag)} data-testid={`agency-settings-${ag.slug}`}>
                          <Settings className="w-4 h-4" />
                        </Button>
                        {isGroupSA && (
                          <Button variant="ghost" size="sm" onClick={() => openEdit(ag)} data-testid={`agency-edit-${ag.slug}`}>
                            <Pencil className="w-4 h-4 text-slate-500" />
                          </Button>
                        )}
                        {isGroupSA && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => toggleActive(ag)}
                            data-testid={`agency-toggle-${ag.slug}`}
                          >
                            {ag.is_active ? <X className="w-4 h-4 text-red-500" /> : <Check className="w-4 h-4 text-emerald-500" />}
                          </Button>
                        )}
                        {isGroupSA && (
                          <Button variant="ghost" size="sm" onClick={() => openDelete(ag)} data-testid={`agency-delete-${ag.slug}`}>
                            <Trash2 className="w-4 h-4 text-red-600" />
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      </Card>

      {/* Settings Dialog */}
      <Dialog open={settingsOpen} onOpenChange={setSettingsOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Parametres — {selectedAgency?.name}</DialogTitle>
            <DialogDescription>Taux de commissions et fonctionnalites par filiale</DialogDescription>
          </DialogHeader>
          {!agencySettings ? (
            <div className="text-center py-8"><Loader2 className="w-5 h-5 animate-spin inline" /></div>
          ) : (
            <div className="space-y-4 py-2">
              <div>
                <p className="font-medium mb-2">Taux de commissions AC (par palier)</p>
                <div className="grid grid-cols-3 gap-3">
                  {[
                    { key: 'seuil_1', label: 'Palier 1 (CA < seuil_1_max)' },
                    { key: 'seuil_2', label: 'Palier 2 (CA >= seuil_1_max)' },
                    { key: 'seuil_3', label: 'Palier 3 (CA >= seuil_2_max)' },
                  ].map(({ key, label }, idx) => (
                    <div key={key} className="space-y-1">
                      <Label className="text-xs">{label} (%)</Label>
                      <Input
                        type="number"
                        step="0.1"
                        value={agencySettings.commission_rates_ac?.[key] ?? [22, 25, 27][idx]}
                        disabled={!isGroupSA}
                        onChange={(e) => setAgencySettings({
                          ...agencySettings,
                          commission_rates_ac: {
                            ...agencySettings.commission_rates_ac,
                            [key]: parseFloat(e.target.value) || 0,
                          },
                        })}
                        data-testid={`ac-rate-${key}`}
                      />
                    </div>
                  ))}
                </div>
                <div className="grid grid-cols-2 gap-3 mt-3">
                  <div className="space-y-1">
                    <Label className="text-xs">Seuil 1 max (€)</Label>
                    <Input
                      type="number"
                      value={agencySettings.commission_thresholds_ac?.tier_1_max ?? 3500}
                      disabled={!isGroupSA}
                      onChange={(e) => setAgencySettings({
                        ...agencySettings,
                        commission_thresholds_ac: {
                          ...agencySettings.commission_thresholds_ac,
                          tier_1_max: parseFloat(e.target.value) || 0,
                        },
                      })}
                      data-testid="ac-threshold-1"
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Seuil 2 max (€)</Label>
                    <Input
                      type="number"
                      value={agencySettings.commission_thresholds_ac?.tier_2_max ?? 6500}
                      disabled={!isGroupSA}
                      onChange={(e) => setAgencySettings({
                        ...agencySettings,
                        commission_thresholds_ac: {
                          ...agencySettings.commission_thresholds_ac,
                          tier_2_max: parseFloat(e.target.value) || 0,
                        },
                      })}
                      data-testid="ac-threshold-2"
                    />
                  </div>
                </div>
              </div>
              <div>
                <p className="font-medium mb-2">Taux de commissions RH (par palier)</p>
                <div className="grid grid-cols-3 gap-3">
                  {[
                    { key: 'tier_1', label: 'Palier 1 (<= tier_1_max AC)' },
                    { key: 'tier_2', label: 'Palier 2 (> tier_1_max AC)' },
                    { key: 'tier_3', label: 'Palier 3 (> tier_2_max AC)' },
                  ].map(({ key, label }, idx) => (
                    <div key={key} className="space-y-1">
                      <Label className="text-xs">{label} (%)</Label>
                      <Input
                        type="number"
                        step="0.1"
                        value={agencySettings.commission_rates_rh?.[key] ?? [7, 8, 9][idx]}
                        disabled={!isGroupSA}
                        onChange={(e) => setAgencySettings({
                          ...agencySettings,
                          commission_rates_rh: {
                            ...agencySettings.commission_rates_rh,
                            [key]: parseFloat(e.target.value) || 0,
                          },
                        })}
                        data-testid={`rh-rate-${key}`}
                      />
                    </div>
                  ))}
                </div>
                <div className="grid grid-cols-2 gap-3 mt-3">
                  <div className="space-y-1">
                    <Label className="text-xs">Seuil 1 (nb AC max)</Label>
                    <Input
                      type="number"
                      value={agencySettings.commission_thresholds_rh?.tier_1_max ?? 6}
                      disabled={!isGroupSA}
                      onChange={(e) => setAgencySettings({
                        ...agencySettings,
                        commission_thresholds_rh: {
                          ...agencySettings.commission_thresholds_rh,
                          tier_1_max: parseInt(e.target.value) || 0,
                        },
                      })}
                      data-testid="rh-threshold-1"
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Seuil 2 (nb AC max)</Label>
                    <Input
                      type="number"
                      value={agencySettings.commission_thresholds_rh?.tier_2_max ?? 12}
                      disabled={!isGroupSA}
                      onChange={(e) => setAgencySettings({
                        ...agencySettings,
                        commission_thresholds_rh: {
                          ...agencySettings.commission_thresholds_rh,
                          tier_2_max: parseInt(e.target.value) || 0,
                        },
                      })}
                      data-testid="rh-threshold-2"
                    />
                  </div>
                </div>
              </div>

              {/* FDV Template selector */}
              <div className="border-t pt-4">
                <label className="text-sm font-semibold text-slate-900 mb-2 block">
                  Formulaire FDV attribue
                </label>
                <Select
                  value={agencySettings.fdv_template_id || '__default__'}
                  onValueChange={(v) => setAgencySettings({
                    ...agencySettings,
                    fdv_template_id: v === '__default__' ? null : v,
                  })}
                  disabled={!isGroupSA}
                >
                  <SelectTrigger className="w-full" data-testid="fdv-template-select">
                    <SelectValue placeholder="Formulaire FDV" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__default__">-- Formulaire par defaut (EduPlan) --</SelectItem>
                    {fdvTemplates.map((t) => (
                      <SelectItem key={t.template_id} value={t.template_id} disabled={!t.montant_total_placed}>
                        {t.name}{!t.montant_total_placed ? ' (montant_total manquant)' : ''}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-slate-500 mt-2">
                  Determine quel formulaire FDV s&apos;affiche pour les commerciaux de cette filiale.
                  Creez et gerez les formulaires dans la section &laquo; Formulaires FDV &raquo;.
                </p>
              </div>

              {!isGroupSA && (
                <p className="text-xs text-amber-600 bg-amber-50 p-2 rounded">
                  Seul le SA Groupe peut modifier ces parametres.
                </p>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setSettingsOpen(false)}>Fermer</Button>
            {isGroupSA && agencySettings && (
              <Button onClick={saveSettings} disabled={submitting} className="bg-indigo-600 hover:bg-indigo-700" data-testid="save-agency-settings-btn">
                {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                Enregistrer
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Agency Dialog */}
      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Modifier la filiale</DialogTitle>
            <DialogDescription>Mettez a jour le nom et l&apos;URL du logo a tout moment.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label>Nom</Label>
              <Input value={editForm.name} onChange={(e) => setEditForm({ ...editForm, name: e.target.value })} data-testid="edit-agency-name-input" />
            </div>
            <div className="space-y-2">
              <Label>URL du logo</Label>
              <Input value={editForm.logo_url} onChange={(e) => setEditForm({ ...editForm, logo_url: e.target.value })} placeholder="https://..." data-testid="edit-agency-logo-input" />
              {editForm.logo_url ? (
                <img src={editForm.logo_url} alt="apercu" className="w-12 h-12 rounded-lg object-cover border mt-2" />
              ) : null}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditOpen(false)}>Annuler</Button>
            <Button onClick={saveEdit} disabled={submitting} className="bg-indigo-600 hover:bg-indigo-700" data-testid="save-agency-edit-btn">
              {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}Enregistrer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Agency Dialog (code confirmation) */}
      <Dialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <AlertTriangle className="w-5 h-5" /> Supprimer la filiale
            </DialogTitle>
            <DialogDescription>
              Cette action est irreversible. La filiale &laquo; {deleteTarget?.name} &raquo;, ses parametres et ses formulaires FDV seront supprimes.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <p className="text-sm text-slate-600">Pour confirmer, saisissez le code ci-dessous :</p>
            <div className="text-center">
              <code className="inline-block px-4 py-2 rounded-lg bg-slate-100 text-lg font-bold tracking-widest text-slate-900" data-testid="delete-agency-code">{deleteCode}</code>
            </div>
            <Input
              value={deleteInput}
              onChange={(e) => setDeleteInput(e.target.value)}
              placeholder="Saisissez le code"
              className="text-center uppercase tracking-widest"
              data-testid="delete-agency-input"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteTarget(null)}>Annuler</Button>
            <Button
              onClick={confirmDelete}
              disabled={submitting || deleteInput.trim().toUpperCase() !== deleteCode}
              className="bg-red-600 hover:bg-red-700"
              data-testid="confirm-delete-agency-btn"
            >
              {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Trash2 className="w-4 h-4 mr-2" />}Supprimer definitivement
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminAgencies;
